import { useEffect, useRef } from 'react';
import { Shield, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TurnstileWidgetProps {
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpired?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'flexible' | 'compact';
  action?: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  className?: string;
}

// Turnstile site key must be provided via env for production.
// NOTE: We intentionally do NOT fallback to Cloudflare test keys here, because
// using a test site key with a real secret key causes "invalid-input-response".
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

if (!TURNSTILE_SITE_KEY) {
  console.error('Turnstile misconfigured: missing VITE_TURNSTILE_SITE_KEY');
}

export const TurnstileWidget = ({
  onVerify,
  onError,
  onExpired,
  theme = 'auto',
  size = 'normal',
  action = 'contact_form',
  appearance = 'always',
  className,
}: TurnstileWidgetProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const isRenderedRef = useRef(false);
  const scriptLoadedRef = useRef(false);
  const callbacksRef = useRef({ onVerify, onError, onExpired });

  useEffect(() => {
    callbacksRef.current = { onVerify, onError, onExpired };
  }, [onVerify, onError, onExpired]);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      callbacksRef.current.onError?.();
      return;
    }

    // Load Turnstile script if not already loaded
    const loadTurnstileScript = () => {
      if (scriptLoadedRef.current || document.querySelector('script[src*="turnstile"]')) {
        return;
      }
      scriptLoadedRef.current = true;

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    loadTurnstileScript();

    let interval: ReturnType<typeof setInterval> | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const renderWidget = () => {
      if (!window.turnstile || !containerRef.current || isRenderedRef.current) return;

      // Clear container
      containerRef.current.innerHTML = '';

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => {
            callbacksRef.current.onVerify(token);
          },
          'error-callback': () => {
            callbacksRef.current.onError?.();
          },
          'expired-callback': () => {
            callbacksRef.current.onExpired?.();
          },
          theme,
          size,
          action,
          appearance,
        });
        isRenderedRef.current = true;
        if (interval) {
          clearInterval(interval);
          interval = undefined;
        }
        if (timeout) {
          clearTimeout(timeout);
          timeout = undefined;
        }
      } catch (e) {
        console.error('Failed to render Turnstile widget:', e);
        if (interval) {
          clearInterval(interval);
          interval = undefined;
        }
        if (timeout) {
          clearTimeout(timeout);
          timeout = undefined;
        }
        callbacksRef.current.onError?.();
      }
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      interval = setInterval(() => {
        if (window.turnstile) {
          if (interval) clearInterval(interval);
          renderWidget();
        }
      }, 100);

      timeout = setTimeout(() => {
        if (interval) clearInterval(interval);
        if (!isRenderedRef.current) {
          callbacksRef.current.onError?.();
        }
      }, 15000);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
        isRenderedRef.current = false;
      }
    };
  }, [action, appearance, theme, size]);

  return (
    <div className={cn('turnstile-container', className)}>
      <div ref={containerRef} />
    </div>
  );
};

// Verified badge component to show after successful verification
export const TurnstileVerifiedBadge = ({ verified }: { verified: boolean }) => {
  // Read language directly from <html lang> to avoid tight coupling with router context.
  const lang =
    (typeof document !== 'undefined' && document.documentElement.lang) || 'en';
  const notVerifiedText: Record<string, string> = {
    en: 'Please verify that you are not a robot',
    tr: 'Lütfen robot olmadığınızı doğrulayın',
    nl: 'Bevestig dat u geen robot bent',
    fr: 'Veuillez vérifier que vous n’êtes pas un robot',
    ar: 'يرجى التحقق من أنك لست روبوتًا',
  };
  const verifiedText: Record<string, string> = {
    en: 'Verified',
    tr: 'Doğrulandı',
    nl: 'Geverifieerd',
    fr: 'Vérifié',
    ar: 'تم التحقق',
  };
  if (!verified) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Shield className="w-4 h-4" />
        <span>{notVerifiedText[lang] ?? notVerifiedText.en}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-green-600">
      <CheckCircle className="w-4 h-4" />
      <span>{verifiedText[lang] ?? verifiedText.en}</span>
    </div>
  );
};
